/**
 * Formatting helpers lifted out of the monolith's script block, where they
 * were scattered across ~5.6k lines and, in a couple of cases (compact
 * numbers, relative time), duplicated with slightly different rounding per
 * view. Consolidating them is a small correctness win on top of the
 * structural one.
 */

/** Order statuses in lifecycle order — the stage track and the route
 *  progress percentage both index into this, so it must stay ordered. */
export const STAGES = ['dispatched', 'loading', 'en_route', 'unloading', 'delivered'];
export const ALL_STATUSES = ['scheduled', ...STAGES, 'cancelled'];

export const stageLabel = (t, status) => (status ? t(`status.${status}`) : '—');

/** Leaflet's bindPopup/bindTooltip set their content via innerHTML whenever
 *  they're handed a string (DivOverlay._updateContent, leaflet-src.js) — so
 *  every value that ultimately originates from a database row (a quarry
 *  name, a driver's name/plate) must be escaped before it reaches one of
 *  those calls, even ones that look "admin-entered": a crafted name is a
 *  stored XSS that fires in an authenticated dispatcher's session the
 *  moment they hover the marker. See frontend/src/components/dispatcher/
 *  LiveMap.jsx and frontend/src/lib/driverPopup.js for the call sites. */
export function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch],
  );
}

/** Dispatchers refer to jobs by waybill number in conversation, so a job
 *  shows its waybill as soon as one exists and a short id until then. */
export function waybillLabel(order) {
  if (order.waybill_number) return order.waybill_number;
  return `#${String(order.order_id ?? '').slice(0, 8)}`;
}

/**
 * The tonnage a client-facing row should print, and whether that number is
 * a weighbridge reading or the dispatcher's plan.
 *
 * Both client surfaces used to render `actual_ocr_weight_tons ?? target_tonnage`
 * bare, so a load nobody had weighed yet showed the quantity the client
 * ORDERED as the quantity that ARRIVED — on the one page a site manager
 * reconciles a supplier's invoice against. The dispatcher's own OrdersTable
 * has always suffixed that same fallback with common.estSuffix; this makes
 * the client's copy say the same thing.
 *
 * GET /api/track/<key> returns weight_is_estimate per row and it is trusted
 * when present, rather than each surface re-deriving "is this real?" from
 * which column happens to be null. `tons` is null (never 0) when there is
 * no number at all — estimated_weight_tons is nullable, and a zero would
 * read as "an empty truck arrived".
 */
export function trackedWeight(order) {
  const measured = order.actual_ocr_weight_tons;
  const isEstimate = order.weight_is_estimate ?? measured == null;
  return {
    tons: (isEstimate ? order.target_tonnage : measured) ?? null,
    isEstimate,
  };
}

/**
 * The backend now emits one format, ISO-8601 UTC with an explicit offset
 * ('2026-08-11T03:33:17+00:00'), which Date() parses correctly everywhere.
 *
 * The space-separated branch stays as a fallback for the shape SQLite's own
 * datetime('now') used to write: Safari rejects that string outright and
 * Chrome reads it as LOCAL time, so every rendered timestamp silently
 * shifted by the viewer's UTC offset — three hours in Lithuania in summer,
 * on a delivery time somebody is reconciling against a paper slip. A
 * database migrated by app.py's _migrate_one_timestamp_format() no longer
 * holds that shape, but a browser holding a cached response from before the
 * upgrade does, and being wrong about a delivery time is not a good trade
 * for deleting two lines.
 */
function toDate(iso) {
  if (!iso) return null;
  const normalized = iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Whole kilometres with a Lithuanian thousands separator, or an em-dash.
 *
 * Not `Number.toLocaleString()`, which formats in the BROWSER's locale and
 * not the product's: a Lithuanian carrier whose Chrome is set to English
 * sees "402,000 km" on the vehicle card and "402 000 km" in the maintenance
 * panel two centimetres below it — the same reading, in two formats, on one
 * screen. Not `formatCompactNumber` either: that collapses 402 000 and
 * 402 400 to the same string, and on a service threshold that difference is
 * the difference between booking the workshop this week and next.
 *
 * A non-breaking space, so a grouped number never wraps across a line.
 */
export function formatKm(value) {
  if (value == null) return '—';
  return `${groupDigits(Math.round(value))} km`;
}

/**
 * Thousands, the way this product writes them: groups separated by a
 * non-breaking space, which is the Lithuanian convention and is NOT what
 * `toLocaleString()` gives you.
 *
 * V-352. The regex was written out twice — here and in eurExact() below —
 * and then a third and fourth place needed it and reached for
 * `toLocaleString('en-US')` instead, which puts a COMMA between the groups.
 * A comma between groups is not merely foreign here: in Lithuanian a comma
 * is the DECIMAL separator, so "€14,400" is read off the screen as fourteen
 * euros forty by the same person for whom eurExact() prints "€14 400,00" on
 * the panel next door. One implementation, so the next place that needs
 * grouped digits has something to call.
 *
 * \u00a0 spelled out rather than typed, for the reason eurExact() gives: a
 * literal non-breaking space in source is invisible, and the one edit that
 * turns it back into a normal space wraps a total across two lines.
 */
export function groupDigits(value) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

/**
 * The clock the DOCUMENT will carry, not the clock this browser is on.
 *
 * V-132. Every timestamp the server stores is UTC. The invoice PDF prints
 * waiting windows on the Lithuanian wall clock (core/local_time.py), because
 * that is the clock the client's own gate log is written in — so a preview of
 * that document has to be pinned to the same zone, not to wherever the
 * dispatcher's laptop happens to be. A dispatcher on holiday in Spain must
 * not read a window three hours away from the one his client will receive.
 *
 * Deliberately different from formatOrderDateTime/formatDate above, which
 * show "when did this happen, for me" and are right to follow the browser.
 * The rule for telling them apart: is this string a preview of something
 * being sent to somebody else? Then it is pinned.
 *
 * The zone is the same assumption core/local_time.py names and holds in one
 * place — the product stores no timezone per tenant.
 */
const DOCUMENT_ZONE = 'Europe/Vilnius';

/** "00:40" on the document's clock, or an em-dash. Never a slice of the ISO
 *  string: `iso.slice(11, 16)` is UTC and reads three hours early in summer. */
export function formatDocumentTime(iso) {
  const d = toDate(iso);
  if (!d) return '—';
  return d.toLocaleTimeString('lt-LT', {
    timeZone: DOCUMENT_ZONE, hour: '2-digit', minute: '2-digit',
  });
}

/** "2026-08-28" on the document's clock, or an em-dash. Same rule as above:
 *  across midnight the slice is a whole day out. */
export function formatDocumentDate(iso) {
  const d = toDate(iso);
  if (!d) return '—';
  return d.toLocaleDateString('sv-SE', { timeZone: DOCUMENT_ZONE });
}

export function formatOrderDateTime(iso, lang = 'lt') {
  const d = toDate(iso);
  if (!d) return iso || '—';
  return d.toLocaleString(lang === 'lt' ? 'lt-LT' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso, lang = 'lt') {
  const d = toDate(iso);
  if (!d) return iso || '—';
  return d.toLocaleDateString(lang === 'lt' ? 'lt-LT' : 'en-GB');
}

/** Minutes elapsed since `iso`, or null when unparseable. */
export function minutesSince(iso) {
  const d = toDate(iso);
  return d ? (Date.now() - d.getTime()) / 60_000 : null;
}

/** A GPS fix older than this is drawn faded — the truck is not necessarily
 *  where the map says it is. Same threshold the monolith used. */
export const STALE_FIX_MINUTES = 30;

export function relativeTime(iso, t) {
  const mins = minutesSince(iso);
  if (mins === null) return '—';
  if (mins < 1) return t('dispatcher.justNow');
  if (mins < 60) return `${Math.floor(mins)} ${t('dispatcher.minShort')}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${t('dispatcher.hourShort')}`;
  return `${Math.floor(hours / 24)} ${t('dispatcher.dayShort')}`;
}

export function formatCompactNumber(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${Math.round(n / 1000)}k`;
  if (abs >= 1000) return `${(n / 1000).toFixed(1)}k`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

export const eur = (n) => (n === null || n === undefined ? '—' : `€${formatCompactNumber(n)}`);
export const pct = (ratio) => (ratio === null || ratio === undefined ? '—' : `${Math.round(ratio * 100)}%`);

/** Rounds an axis maximum up to a readable 1/2/5 × 10ⁿ step. */
export function niceMax(value) {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Days until an ISO date (negative = already expired). Drives the
 *  compliance badges on vehicles and drivers, and the driver PWA's own
 *  expiry banner.
 *
 *  V-159. "Today" is the viewer's LOCAL calendar date, not UTC's. Every
 *  driver and dispatcher using this is physically in Lithuania, which runs
 *  ahead of UTC year-round — so for the first two or three hours of every
 *  local day, `getUTCFullYear/Month/Date()` would still answer with
 *  yesterday's date and under-count how expired a document already is.
 *  That is the one direction of error a compliance warning must not make. */
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const todayLocal = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - todayLocal) / 86_400_000);
}

/**
 * The first and last day of "this month, offset by `offset` months" — the
 * viewer's LOCAL calendar month, as YYYY-MM-DD — plus `anchor`, a UTC-
 * midnight Date for that month's 1st, for display formatting.
 *
 * V-202. Same mistake `daysUntil()` above exists to prevent, found a second
 * time (LeavesView.jsx's own copy, since fixed to call this instead): read
 * the CURRENT date with the browser's LOCAL getters, then hand Date.UTC()
 * the result only to serialize it safely — never read `now` back through
 * the UTC getters, which answer the UTC calendar MONTH of the current
 * instant. For the first two or three hours of every Vilnius day that is
 * still last month, and driver_vacations.start_date/end_date are plain
 * calendar dates with no UTC semantics — unlike monthRange(month) below,
 * which is deliberately UTC because it bounds a search against
 * core/utils.now_iso()-stamped invoice timestamps. Not the same function
 * despite the similar name: one takes an explicit month, this one asks
 * what month it is right now.
 */
export function localMonthRange(offset) {
  const now = new Date();
  const first = new Date(Date.UTC(now.getFullYear(), now.getMonth() + offset, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last), anchor: first };
}

/**
 * The first and last day of a YYYY-MM month, as YYYY-MM-DD.
 *
 * Day 0 of the NEXT month is the last day of this one, which handles
 * February and leap years without a table. Built in UTC because the server
 * compares these against issued_at, which core/utils.now_iso() stamps in
 * UTC — a local-time month boundary would put an invoice issued late on the
 * 31st into the wrong month's file.
 *
 * null for anything that is not a month, so a caller can disable its button
 * rather than requesting a range the server will refuse.
 */
export function monthRange(month) {
  const [year, monthNumber] = String(month ?? '').split('-').map(Number);
  if (!year || !monthNumber || monthNumber < 1 || monthNumber > 12) return null;
  const last = new Date(Date.UTC(year, monthNumber, 0));
  return { from: `${month}-01`, to: last.toISOString().slice(0, 10) };
}

/**
 * The month before the one `today` falls in, as YYYY-MM.
 *
 * V-207. Same mistake `localMonthRange()` above was fixed for (V-202, which
 * flagged this exact function as an unverified lead rather than claiming it
 * on the spot): read `today` back through getUTCFullYear()/getUTCMonth() —
 * the UTC calendar month of the current instant, not the viewer's. For the
 * first two or three hours of every Vilnius day that is still last month,
 * so the invoice export panel opened on the 1st could default to two months
 * back instead of one. Confirmed NOT the same UTC convention monthRange()
 * below deliberately uses: that function turns an already-chosen "YYYY-MM"
 * label into a date range compared against issued_at, a real UTC instant —
 * this one decides what "now" is, which core/invoice_export.py's own
 * _vilnius_midnight_utc() (V-173) already treats as a Vilnius calendar
 * question, not a UTC one.
 */
export function previousMonth(today = new Date()) {
  const first = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
  first.setUTCMonth(first.getUTCMonth() - 1);
  return first.toISOString().slice(0, 7);
}

/**
 * Money to the cent, or an em-dash. The invoicing screen's formatter.
 *
 * Deliberately NOT eur() above. That one runs through formatCompactNumber,
 * so €1 234,56 comes out as "€1.2k" — right for an analytics tile, where
 * the reader is judging a trend and a hundred euros either way changes
 * nothing, and wrong for a document somebody sends a client. An invoice
 * total on screen that does not match the invoice is not a rounding
 * choice, it is a second figure.
 *
 * The separators are written here rather than taken from toLocaleString so
 * the same amount prints identically in both languages and in every
 * runtime: the number a dispatcher reads off this screen and the number
 * they type into an accounting system have to be the same string. Grouped
 * with a non-breaking space and a comma decimal — the Lithuanian
 * convention, and the dispatcher reads Lithuanian.
 *
 * null is an em-dash, never 0,00. core/invoicing.order_revenue() returns
 * None rather than 0.0 for exactly this reason: a free delivery and an
 * unpriced one are different facts, and only one of them is worth nothing.
 */
/**
 * One typed decimal, read back as a number — or NaN when the box does not
 * hold a finished one.
 *
 * V-339. eurExact() above prints €12,50 because "the dispatcher reads
 * Lithuanian", and an `inputMode="decimal"` box on an lt-locale phone puts
 * a COMMA on the decimal key. `Number.parseFloat('12,50')` is 12 — not an
 * error, not NaN, just quietly four percent less on every load of that
 * material until somebody reopens the page. Twelve decimal boxes across the
 * price list and the two rate-tier screens parsed a raw value that way; the
 * cost box in the very same row already folded the comma, and its test is
 * named for it.
 *
 * Trailing/leading space is trimmed because a pasted figure carries it.
 * Everything else is left to Number.parseFloat deliberately: this is the
 * comma fix, not a validator. Callers already test Number.isNaN /
 * Number.isFinite and say which box is wrong (see QuarryPriceList's add()),
 * and core/data/TenantSettings.jsx's FINISHED_DECIMAL is the stricter road
 * for the screens that need one — its own comment explains why parsing
 * alone is too forgiving to lean on there.
 */
export function parseDecimalInput(raw) {
  if (raw === null || raw === undefined) return Number.NaN;
  return Number.parseFloat(String(raw).trim().replace(',', '.'));
}


export function eurExact(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const [whole, cents] = Math.abs(value).toFixed(2).split('.');
  return `${value < 0 ? '-' : ''}€${groupDigits(whole)},${cents}`;
}
